import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import {
  UIButton,
  UIFormSectionCard,
  UIProgressBar,
  UIRating,
  UIStatusTag,
  UITextarea,
} from "../../../ui";
import EvaluationApplicantCard from "./EvaluationApplicantCard";
import messages from "../../../../assets/locale/messages";
import {
  formatEventStatusTabLabel,
  showErrorMsg,
  showSuccessMsg,
} from "../../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { adminPermissionModules } from "../../../../utils/Constants";
import { useAdminModulePermission } from "../../../../utils/adminPortalHelpers";
import {
  getCompanyEvaluationForm,
  submitCompanyEvaluationForm,
} from "../../../../network/apis/companyPortalEvaluations";
import {
  getViewFormDetails,
  getViewFormDetailsSupervisor,
  submitEvaluation,
} from "../../../../network/apis/evaluation";
import { isCompanyOwner, isSupervisorManger } from "../../../../utils/Permissions";
import { EVENT_TABS } from "../";
import { normalizeEvaluationFormDetails } from "./evaluationMappers";
import {
  APPLICANT_EVALUATION_STATUSES,
  EVALUATION_FEEDBACK_MAX_LENGTH,
} from "./constants";

const buildRatings = (questions = []) =>
  questions.reduce((acc, q) => ({ ...acc, [String(q.id)]: q.rate || 0 }), {});

const ApplicantEvaluationResult = () => {
  const history = useHistory();
  const { state } = useLocation();
  const { applicant, round, event } = state || {};
  const isAdminPortal = state?.portalVariant === "admin-portal";
  // The event Supervisor is admin-portal but authenticates via the UserApp realm.
  const isSupervisor = isSupervisorManger();
  const eventsListPath = isAdminPortal
    ? ROUTE_PATHS.events
    : ROUTE_PATHS.companyEvents;
  const viewEventPath = isAdminPortal
    ? ROUTE_PATHS.viewEvent
    : ROUTE_PATHS.companyViewEvent;
  const lang = useSelector((s) => s.locale.lang);
  const login = useSelector((s) => s.auth.login);
  const { events, shared } = messages[lang];
  const tForm = events.viewEvents.evaluationsTab.applicantsView.evaluateForm;
  const tResult = events.viewEvents.evaluationsTab.applicantsView.viewResultForm;
  const { canEdit: canEditAdminEvaluations } = useAdminModulePermission(
    adminPermissionModules.evaluations,
  );

  const [details, setDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [ratings, setRatings] = useState({});
  const [feedback, setFeedback] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!applicant?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const fetcher = isSupervisor
          ? getViewFormDetailsSupervisor
          : isAdminPortal
          ? getViewFormDetails
          : getCompanyEvaluationForm;
        const res = await fetcher(applicant.id);
        if (!cancelled) {
          setDetails(normalizeEvaluationFormDetails(res?.data?.data ?? {}));
        }
      } catch (e) {
        if (!cancelled) showErrorMsg(e.response?.data?.message || shared.generalErrMsg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicant, isAdminPortal, isSupervisor, shared.generalErrMsg]);

  if (!applicant || !round) {
    history.replace(eventsListPath);
    return null;
  }

  const handleNavigateToEvent = () => {
    if (!event?.id) return;
    history.push(viewEventPath.replace(":id", event.id));
  };

  const handleNavigateToEvaluations = () => {
    if (!event?.id) return;
    history.push({
      pathname: viewEventPath.replace(":id", event.id),
      state: { initialTab: EVENT_TABS.evaluations },
    });
  };

  const handleNavigateToRound = () => {
    if (!event?.id) return;
    history.push({
      pathname: viewEventPath.replace(":id", event.id),
      state: { initialTab: EVENT_TABS.evaluations, selectedRound: round },
    });
  };

  const breadcrumbList = [
    {
      label: events.listEvents.listEventsBreadcrumb,
      path: eventsListPath,
    },
    {
      label: formatEventStatusTabLabel(event?.status, events.listEvents.eventTabs),
      path: event?.status
        ? `${eventsListPath}?tab=${event.status}`
        : eventsListPath,
    },
    {
      label: event?.name || "",
      path: "",
      isClicked: true,
      onclick: handleNavigateToEvent,
    },
    {
      label: events.viewEvents.evaluations,
      path: "",
      isClicked: true,
      onclick: handleNavigateToEvaluations,
    },
    {
      label: round?.name || "",
      path: "",
      isClicked: true,
      onclick: handleNavigateToRound,
    },
    {
      label: applicant.applicantName,
    },
  ];

  const satisfaction = details?.overallSatisfactionPercentage ?? 0;

  const isDone = details?.status === APPLICANT_EVALUATION_STATUSES.done;
  const statusLabel = isDone ? tResult.statusCompleted : tResult.statusInProgress;
  const statusTone = isDone ? "successSoft" : "warningSoft";

  const questions = details?.questions || [];
  // A form is editable only once submitted, and only by the Super Admin (or an
  // admin role holding the Evaluations edit permission) and the Company Admin
  // (owner). The event Supervisor authenticates via the UserApp realm and is
  // excluded.
  const canEditEvaluation =
    isDone &&
    !isSupervisor &&
    (isAdminPortal ? canEditAdminEvaluations : isCompanyOwner(login));

  const handleStartEdit = () => {
    setRatings(buildRatings(questions));
    setFeedback(details?.comment || "");
    setIsEditing(true);
  };

  const handleRatingChange = (questionId, value) => {
    setRatings((prev) => ({ ...prev, [String(questionId)]: value }));
  };

  // A submitted form must keep every indicator rated — the API rejects rate 0.
  const isSaveDisabled = isSaving || questions.some((q) => !ratings[String(q.id)]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const submit = isAdminPortal ? submitEvaluation : submitCompanyEvaluationForm;
      const res = await submit({
        id: applicant.id,
        data: {
          comment: feedback,
          evaluation_form_questions_attributes: questions.map((q) => ({
            id: q.id,
            rate: ratings[String(q.id)],
          })),
        },
      });
      setDetails(normalizeEvaluationFormDetails(res?.data?.data ?? {}));
      setIsEditing(false);
      showSuccessMsg(res?.data?.message || tResult.saveSuccess, true);
    } catch (e) {
      showErrorMsg(e.response?.data?.message || shared.generalErrMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box
      sx={{
        marginTop: "var(--space-32)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-16)",
        paddingBottom: "var(--space-32)",
      }}
    >
      <Box className="eval-result">
        <Breadcrumbs list={breadcrumbList} />

        <Box className="eval-result__header">
          <Box className="eval-result__header-text">
            <Typography className="eval-result__title text-heading-xl">
              {tForm.title}
            </Typography>
            <Typography className="eval-result__subtitle text-body-sm">
              {tForm.subtitle.replace("{roundName}", round?.name || "")}
            </Typography>
          </Box>
          {canEditEvaluation && !isEditing && (
            <UIButton
              variant="primary"
              appearance="outline"
              size="medium"
              onClick={handleStartEdit}
            >
              {tResult.editEvaluation}
            </UIButton>
          )}
        </Box>

        <EvaluationApplicantCard applicant={applicant}>
          <Box className="eval-result__satisfaction-section">
            <Box className="eval-result__satisfaction-row">
              <Box className="eval-result__satisfaction-left">
                <Typography className="eval-result__satisfaction-label">
                  {tResult.overallSatisfaction}
                </Typography>
                <Typography className="eval-result__satisfaction-pct">
                  {satisfaction}%
                </Typography>
              </Box>
              <Box className="eval-result__bar-wrapper">
                <UIProgressBar value={satisfaction} showDot />
                <Box className="eval-result__bar-labels">
                  <Typography className="eval-result__bar-label">0%</Typography>
                  <Typography className="eval-result__bar-label">50%</Typography>
                  <Typography className="eval-result__bar-label">100%</Typography>
                </Box>
              </Box>
              <Box className="eval-result__status-col">
                <Typography className="eval-result__satisfaction-label">
                  {tForm.evaluationStatus}
                </Typography>
                <UIStatusTag
                  status={details?.status || APPLICANT_EVALUATION_STATUSES.pending}
                  label={statusLabel}
                  tone={statusTone}
                  preset="table"
                  sx={{
                    borderRadius: "9999px",
                    border: "1px solid",
                    height: "auto",
                    "& .MuiChip-label": { padding: "4px 12px" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </EvaluationApplicantCard>

        <UIFormSectionCard
          title={tForm.performanceIndicators}
          action={
            isEditing ? (
              <Typography className="eval-applicant-form__hint text-label-sm">
                {tResult.clickToReRate}
              </Typography>
            ) : undefined
          }
        >
          <Box className="eval-applicant-form__criteria-grid">
            {questions.map((q) => (
              <Box key={q.id} className="eval-applicant-form__criteria-item">
                <Typography className="eval-applicant-form__criteria-label">
                  {q.body}
                </Typography>
                {isEditing ? (
                  <UIRating
                    value={ratings[String(q.id)] || 0}
                    onChange={(value) => handleRatingChange(q.id, value)}
                  />
                ) : (
                  <UIRating value={q.rate || 0} />
                )}
              </Box>
            ))}
          </Box>
        </UIFormSectionCard>

        <UIFormSectionCard title={tForm.detailedFeedback}>
          {isEditing ? (
            <UITextarea
              placeholder={tForm.feedbackPlaceholder}
              value={feedback}
              onValueChange={setFeedback}
              inputProps={{ maxLength: EVALUATION_FEEDBACK_MAX_LENGTH }}
              fullWidth
              minRows={5}
            />
          ) : (
            <Typography className="eval-result__feedback-text text-body-sm">
              {details?.comment || tResult.noFeedback}
            </Typography>
          )}
        </UIFormSectionCard>

        {isEditing && (
          <UIFormSectionCard>
            <Box className="eval-applicant-form__footer">
              <Box className="eval-applicant-form__footer-status">
                <Typography className="eval-applicant-form__footer-status-label">
                  {tForm.evaluationStatus}
                </Typography>
                <UIStatusTag
                  status={details?.status || APPLICANT_EVALUATION_STATUSES.pending}
                  label={statusLabel}
                  tone={statusTone}
                  preset="table"
                />
              </Box>
              <Box className="eval-applicant-form__footer-actions">
                <UIButton
                  variant="outline"
                  size="medium"
                  onClick={() => setIsEditing(false)}
                >
                  {tResult.cancel}
                </UIButton>
                <UIButton
                  appearance="gradient"
                  size="medium"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                  {tResult.saveChanges}
                </UIButton>
              </Box>
            </Box>
          </UIFormSectionCard>
        )}
      </Box>
    </Box>
  );
};

export default ApplicantEvaluationResult;
